import PageHero from "@/components/site/PageHero";
import { Link } from "react-router-dom";
import { ArrowRight, Compass, Eye, Target } from "lucide-react";

const VALUES = [
  { title: "Practical over theoretical", copy: "Our recommendations survive contact with real payroll cycles, real audits, and real employees." },
  { title: "Compliance without fear", copy: "Regulations are guardrails, not obstacles. We help you use them to your advantage." },
  { title: "Executive presence", copy: "We show up in the boardroom the same way we show up in the breakroom — prepared and respectful." },
  { title: "Discretion, always", copy: "Employee relations, executive transitions, and sensitive workforce decisions demand a partner who keeps confidence." },
];

export default function AboutPage() {
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About Groman Associates"
        title="Executive HR leadership, engineered for the way your organization actually operates."
        subtitle="We founded Groman Associates on a simple premise: growing organizations, mission-driven nonprofits, and government contractors deserve the same caliber of HR leadership as Fortune 500 companies — without the overhead of a full-time C-suite hire."
        breadcrumbs={[{ label: "Home", to: "/" }, { label: "About" }]}
        image="https://images.unsplash.com/photo-1557804506-669a67965ba0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBvZmZpY2UlMjBtZWV0aW5nfGVufDB8fHx8MTc4MzczNDE5MHww&ixlib=rb-4.1.0&q=85"
      />

      {/* Story */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-4">
          <div className="overline mb-4">Our Story</div>
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-charcoal tracking-tight leading-tight">Born in St. Louis. Built for organizations that mean business.</h2>
        </div>
        <div className="lg:col-span-7 lg:col-start-6 space-y-6 text-charcoal-soft text-base md:text-lg leading-relaxed">
          <p>Groman Associates was founded to close a gap we saw repeatedly across the Midwest and beyond: capable organizations — hospitals, nonprofits, professional services firms, and government contractors — running critical people operations without the strategic HR leadership they truly needed.</p>
          <p>The alternatives were either expensive full-time CHRO hires or transactional payroll vendors. Neither fit. So we built a firm that delivers CHRO-level counsel, compliance expertise, and workforce solutions on an engagement model that scales with our clients — from a single compliance audit to a multi-year fractional HR partnership.</p>
          <p>Today we support commercial and government clients across healthcare, nonprofit, professional services, and small-to-mid-sized businesses navigating growth, regulation, and workforce change.</p>
        </div>
      </section>

      {/* Mission Vision Values */}
      <section className="bg-offwhite border-y border-gray-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-3 gap-0 grid-borders">
          <div className="p-10 bg-white">
            <Target className="w-6 h-6 text-gold" />
            <div className="mt-8 overline">Mission</div>
            <p className="mt-3 font-heading text-xl font-medium text-charcoal leading-snug">Help organizations build stronger, more compliant, and more human workforces.</p>
          </div>
          <div className="p-10 bg-white">
            <Eye className="w-6 h-6 text-gold" />
            <div className="mt-8 overline">Vision</div>
            <p className="mt-3 font-heading text-xl font-medium text-charcoal leading-snug">The most trusted HR partner for growing commercial and government organizations in the Midwest.</p>
          </div>
          <div className="p-10 bg-white">
            <Compass className="w-6 h-6 text-gold" />
            <div className="mt-8 overline">How We Work</div>
            <p className="mt-3 font-heading text-xl font-medium text-charcoal leading-snug">Executive counsel, on-the-ground execution, and full accountability for outcomes — not effort.</p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28">
        <div className="overline mb-4">Our Values</div>
        <h2 className="font-heading text-3xl md:text-5xl font-semibold tracking-tight text-charcoal max-w-3xl">Four commitments that show up in every engagement.</h2>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-0 grid-borders">
          {VALUES.map((v, i) => (
            <div key={v.title} className="p-10 bg-white">
              <div className="font-mono text-xs text-gold">0{i + 1}</div>
              <div className="mt-6 font-heading text-xl font-medium text-charcoal">{v.title}</div>
              <p className="mt-3 text-sm text-charcoal-soft leading-relaxed">{v.copy}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Leadership */}
      <section className="bg-charcoal text-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-24 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="overline text-gold mb-4">Leadership</div>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight leading-tight">Two decades of HR leadership across regulated industries.</h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 space-y-5 text-white/75 leading-relaxed">
            <p>Our leadership brings CHRO and senior HR experience across healthcare systems, nonprofit organizations, professional services firms, and federal contract environments. That combination is rare — and it's exactly what our clients need when workforce decisions carry both business and regulatory weight.</p>
            <p>Detailed leadership bios and credentials are available on request during the engagement discussion. We keep our team small, senior, and directly accountable to every client.</p>
            <Link to="/contact" data-testid="about-leadership-cta" className="inline-flex items-center gap-2 mt-6 bg-gold text-white px-6 py-4 text-sm font-medium hover:bg-gold-hover transition-colors">Talk to Our Leadership <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
